package com.mencanhealth.admin.ui

import androidx.compose.runtime.*
import com.mencanhealth.admin.data.AdminChatService
import com.mencanhealth.admin.model.ChatMessage
import com.mencanhealth.admin.model.ChatSummary
import dev.gitlive.firebase.firestore.DocumentSnapshot
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.attributes.*
import org.jetbrains.compose.web.dom.*

@Composable
fun ChatPage(chatService: AdminChatService) {
    var chats by remember { mutableStateOf<List<ChatSummary>>(emptyList()) }
    var lastDocument by remember { mutableStateOf<DocumentSnapshot?>(null) }
    var hasMore by remember { mutableStateOf(false) }
    var selectedChat by remember { mutableStateOf<String?>(null) }
    var messages by remember { mutableStateOf<List<ChatMessage>>(emptyList()) }
    var replyText by remember { mutableStateOf("") }
    var loading by remember { mutableStateOf(true) }
    var loadingMore by remember { mutableStateOf(false) }
    val scope = rememberCoroutineScope()

    LaunchedEffect(Unit) {
        val page = chatService.listChatsPaginated()
        chats = page.chats
        lastDocument = page.lastDocument
        hasMore = page.hasMore
        loading = false
    }

    LaunchedEffect(selectedChat) {
        selectedChat?.let { userId ->
            chatService.observeMessages(userId).collect { msgs ->
                messages = msgs
            }
        }
    }

    fun refreshChats() {
        scope.launch {
            val page = chatService.listChatsPaginated()
            chats = page.chats
            lastDocument = page.lastDocument
            hasMore = page.hasMore
        }
    }

    fun loadMore() {
        if (loadingMore || !hasMore) return
        loadingMore = true
        scope.launch {
            val page = chatService.listChatsPaginated(afterDocument = lastDocument)
            chats = chats + page.chats
            lastDocument = page.lastDocument
            hasMore = page.hasMore
            loadingMore = false
        }
    }

    Div({ classes("chat-layout") }) {
        // Left panel - conversation list
        Div({ classes("chat-list") }) {
            H3 { Text("Conversaciones") }
            if (loading) {
                P { Text("Cargando...") }
            } else if (chats.isEmpty()) {
                P({ classes("empty-state") }) { Text("No hay conversaciones") }
            } else {
                chats.forEach { chat ->
                    Div({
                        classes("chat-item")
                        if (selectedChat == chat.userId) classes("chat-item-active")
                        onClick { selectedChat = chat.userId }
                    }) {
                        Div({ classes("chat-item-name") }) { Text(chat.userName) }
                        Div({ classes("chat-item-preview") }) {
                            Text(chat.lastMessage.take(50))
                        }
                    }
                }
                if (hasMore) {
                    Div({
                        classes("chat-load-more")
                        onClick { loadMore() }
                    }) {
                        Text(if (loadingMore) "Cargando..." else "Cargar más")
                    }
                }
            }
        }

        // Right panel - message thread
        Div({ classes("chat-thread") }) {
            if (selectedChat == null) {
                Div({ classes("empty-state") }) {
                    Text("Selecciona una conversación")
                }
            } else {
                val chatName = chats.find { it.userId == selectedChat }?.userName ?: selectedChat!!
                H3 { Text(chatName) }

                Div({ classes("messages-container") }) {
                    messages.forEach { msg ->
                        Div({
                            classes("message")
                            classes(if (msg.sender == "nutritionist") "message-sent" else "message-received")
                        }) {
                            Div({ classes("message-text") }) { Text(msg.text) }
                            Div({ classes("message-meta") }) {
                                Text(formatTimestamp(msg.timestamp))
                            }
                        }
                    }
                }

                Div({ classes("reply-bar") }) {
                    Input(InputType.Text) {
                        classes("reply-input")
                        value(replyText)
                        attr("placeholder", "Escribe una respuesta...")
                        onInput { replyText = it.value }
                        onKeyDown {
                            if (it.key == "Enter" && replyText.isNotBlank()) {
                                val text = replyText
                                replyText = ""
                                scope.launch {
                                    chatService.sendReply(selectedChat!!, text)
                                    refreshChats()
                                }
                            }
                        }
                    }
                    Button({
                        classes("btn-send")
                        onClick {
                            if (replyText.isNotBlank()) {
                                val text = replyText
                                replyText = ""
                                scope.launch {
                                    chatService.sendReply(selectedChat!!, text)
                                    refreshChats()
                                }
                            }
                        }
                    }) { Text("Enviar") }
                }
            }
        }
    }
}

private fun formatTimestamp(millis: Long): String {
    if (millis == 0L) return ""
    val date = kotlin.js.Date(millis.toDouble())
    return "${date.toLocaleDateString()}  ${date.toLocaleTimeString()}"
}
